import { type FC, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { parsePhoneNumber } from 'libphonenumber-js';
import styled from 'styled-components';
import * as Yup from 'yup';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Text18Medium, Text48Medium, TextInfo12 } from '../../mixins/mixins';
import type { IForm } from './types';
import emailjs from '@emailjs/browser';

const SERVICE_ID = 'service_6xzirzt';
const TEMPLATE_ID = 'template_ze1f45g';
const PUBLIC_KEY = 'yLRnSXLeahRCecptv';

const FormComponent = styled.form<{ formIsVisible: boolean | undefined }>`
  position: relative;
  display: flex;
  width: ${props => (props.formIsVisible ? '100%' : 'unset')};
  flex-direction: column;
  align-items: center;
  padding: ${props => (props.formIsVisible ? 0 : '96px 24px 56px')};
  background: rgba(7, 24, 43, 1);
  @media (max-width: 669px) {
    padding: ${props => (props.formIsVisible ? 0 : '64px 12px 24px')};
  }
`;

const FormWrapper = styled.div<{ formIsVisible: boolean | undefined }>`
  width: ${props => (props.formIsVisible ? '100%' : '1380px')};
  display: flex;
  flex-direction: column;
  @media (max-width: 1440px) {
    width: 100%;
  }
`;

const Title = styled.div`
  ${Text48Medium};
  @media (max-width: 669px) {
    ${Text18Medium};
  }
`;

const InputWrapper = styled.div<{ formIsVisible: boolean | undefined }>`
  display: flex;
  width: 100%;
  flex-direction: column;
  margin-top: ${props => (props.formIsVisible ? '72px' : '120px')};
  @media (max-width: 669px) {
    margin-top: 48px;
  }
`;

const ContactUser = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  @media (max-width: 669px) {
    flex-direction: column;
  }
`;

const FormItem = styled.div`
  width: 100%;
  margin-right: 24px;

  :last-child {
    margin-right: 0;
  }

  @media (max-width: 669px) {
    margin-bottom: 12px;
  }
`;

const Input = styled.input<{ isError: boolean }>`
  width: 100%;
  height: 64px;
  border: ${props =>
    `1px solid ${props.isError ? '#FF0074' : 'rgba(255, 255, 255, 0.6)'}`};
  border-radius: 32px;
  padding-left: 24px;
  color: white;
  ${Text18Medium};

  :last-child {
    margin-right: 0;
  }

  ::placeholder {
    color: rgba(255, 255, 255, 0.6);
    ${Text18Medium};
    @media (max-width: 669px) {
      ${TextInfo12};
    }
  }

  @media (max-width: 669px) {
    height: 40px;
    ${TextInfo12};
    border-radius: 16px;
  }
`;

const AboutUser = styled.textarea<{ isError: boolean }>`
  width: 100%;
  height: 192px;
  resize: none;
  margin-top: 36px;
  border: ${props =>
    `1px solid ${props.isError ? '#FF0074' : 'rgba(255, 255, 255, 0.6)'}`};
  border-radius: 32px;
  color: white;
  padding: 24px;
  ${Text18Medium};

  ::placeholder {
    color: rgba(255, 255, 255, 0.6);
    ${Text18Medium};
    @media (max-width: 669px) {
      ${TextInfo12};
    }
  }

  @media (max-width: 669px) {
    margin-top: 0;
    ${TextInfo12};
    border-radius: 16px;
  }
`;

const SubmitButton = styled.button<{
  isValid: boolean;
  formIsVisible: boolean | undefined;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 448px;
  height: 64px;
  background: ${props =>
    props.isValid ? 'rgba(88, 255, 126, 0.4)' : '#58FF7E'};
  color: black;
  border-radius: 32px;
  margin-left: auto;
  margin-top: ${props => (props.formIsVisible ? '64px' : '131px')};
  ${Text18Medium};
  cursor: pointer;
  @media (max-width: 669px) {
    width: 100%;
    margin-top: 64px;
    ${TextInfo12};
    height: 40px;
  }
`;

const ErrorInfo = styled.div<{ isError: boolean; isMobile: boolean }>`
  visibility: ${props => (props.isError ? 'visible' : 'hidden')};
  display: ${props => (props.isMobile && props.isError ? 'block' : 'none')};
  color: #ff0074;
  font-size: 12px;
  margin-top: 12px;
  @media (max-width: 669px) {
    margin-top: 6px;
  }
`;

const FormSent = styled.div<{ formModal: boolean | undefined; height: number }>`
  position: fixed;
  width: 100vw;
  height: ${props => props.height + 'px'};
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #07182b;
  z-index: 99999999999;

  .wrapperSuccess {
    display: flex;
    align-items: center;
    justify-content: center;
    @media (max-width: 669px) {
      flex-direction: column;
    }
  }

  @media (max-width: 669px) {
    margin-top: ${props => (props.formModal ? '55px' : 0)};
  }
`;

const FormIcon = styled.div`
  @media (max-width: 669px) {
    margin-bottom: 12px;
  }
`;

const FormStatusText = styled.div`
  font-weight: 500;
  font-size: 32px;
  line-height: 140%;
  letter-spacing: -0.01em;
  text-align: center;
  padding: 0 12px;
  color: white;
  @media (max-width: 669px) {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
  }
`;

const Form: FC<IForm> = ({
  formIsVisible,
  status,
  setStatus,
  isVisible,
}) => {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const doc = document.documentElement;
    setHeight(doc.offsetHeight);
  }, []);

  const isMobile = useIsMobile();
  const formik = useFormik({
    initialValues: {
      telegram: '',
      email: '',
      phone: '',
      message: '',
    },
    validationSchema: Yup.object().shape(
      {
        telegram: Yup.string()
          .max(40, 'Maximum 40 characters')
          .when(['email', 'phone'], (values, schema: Yup.StringSchema) => {
            const [email, phone] = values as [string, string];
            return !(email || phone)
              ? schema.required('Error, please fill in at least one field')
              : schema;
          }),
        email: Yup.string()
          .email('Incorrect email')
          .when(['phone', 'telegram'], (values, schema: Yup.StringSchema) => {
            const [phone, telegram] = values as [string, string];
            return !(phone || telegram)
              ? schema.required('Error, please fill in at least one field')
              : schema;
          }),
        phone: Yup.string().when(['email', 'telegram'], (values, schema: Yup.StringSchema) => {
          const [email, telegram] = values as [string, string];
          return !(email || telegram)
            ? schema
              .test('isValid', 'Invalid phone number', (phone: string | undefined) => {
                if (!phone || phone.length < 8) return false;
                try {
                  return parsePhoneNumber(phone).isValid();
                } catch {
                  return false;
                }
              })
              .required('Error, please fill in at least one field')
            : schema.test('isValid', 'Invalid phone number', (phone: string | undefined) => {
              if (!phone) return true;
              if (phone.length < 8) return false;
              try {
                return parsePhoneNumber(phone).isValid();
              } catch {
                return false;
              }
            });
        }),
        message: Yup.string()
          .min(20, 'Please enter at least 20 characters')
          .required('Error, field is required'),
      },
      [
        ['email', 'phone'],
        ['email', 'telegram'],
        ['phone', 'telegram'],
      ],
    ),
    onSubmit: async values => {
      const templateParams = {
        telegram: values.telegram || '-',
        email: values.email || '-',
        phone: values.phone || '-',
        message: values.message || '-',
      };

      try {
        const response = await emailjs.send(
          SERVICE_ID,
          TEMPLATE_ID,
          templateParams,
          PUBLIC_KEY
        );

        if (response.status === 200) {
          setStatus('formSent');
          formik.resetForm();
        } else {
          setStatus('formNoSent');
        }
      } catch (error) {
        setStatus('formNoSent');
        console.error('EmailJS error:', error);
      }
    },
  });
  return (
    <FormComponent
      onSubmit={formik.handleSubmit}
      formIsVisible={formIsVisible}
      id="feedback"
    >
      {status === 'default' && (
        <FormWrapper formIsVisible={formIsVisible}>
          <Title>Submit a request</Title>
          <InputWrapper formIsVisible={formIsVisible}>
            <ContactUser>
              <FormItem>
                <Input
                  placeholder="Telegram"
                  id="telegram"
                  name="telegram"
                  type="text"
                  isError={!!formik.errors.telegram}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.telegram}
                />
                <ErrorInfo
                  isMobile={isMobile}
                  isError={!!formik.touched.telegram && !!formik.errors.telegram}
                >
                  {formik.errors.telegram || 'Ok'}
                </ErrorInfo>
              </FormItem>
              <FormItem>
                <Input
                  placeholder="E-mail"
                  id="email"
                  name="email"
                  type="email"
                  isError={!!formik.errors.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
                <ErrorInfo
                  isMobile={isMobile}
                  isError={!!formik.touched.email && !!formik.errors.email}
                >
                  {formik.errors.email || 'Ok'}
                </ErrorInfo>
              </FormItem>
              <FormItem>
                <Input
                  placeholder="Phone"
                  id="phone"
                  name="phone"
                  type="text"
                  isError={!!formik.errors.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phone}
                />
                <ErrorInfo
                  isMobile={isMobile}
                  isError={!!formik.touched.phone && !!formik.errors.phone}
                >
                  {formik.errors.phone || 'Ok'}
                </ErrorInfo>
              </FormItem>
            </ContactUser>
            <AboutUser
              placeholder="Message"
              id="message"
              name="message"
              isError={!!formik.errors.message}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.message}
            />
            <ErrorInfo
              isMobile={isMobile}
              isError={!!formik.touched.message && !!formik.errors.message}
            >
              {formik.errors.message || 'Ok'}
            </ErrorInfo>
          </InputWrapper>
          <SubmitButton
            formIsVisible={formIsVisible}
            type="submit"
            isValid={!(formik.isValid && formik.dirty)}
          >
            Send request
          </SubmitButton>
        </FormWrapper>
      )}
      {status === 'formSent' && (
        <FormSent formModal={isVisible} height={height}>
          <div className="wrapperSuccess">
            <FormIcon>
              <img src="/assets/img/FooterImages/success.svg" alt="success" />
            </FormIcon>
            <FormStatusText>Message sent successfully</FormStatusText>
          </div>
        </FormSent>
      )}
      {status === 'formNoSent' && (
        <FormSent formModal={isVisible} height={height}>
          <div className="wrapperSuccess">
            <FormIcon>
              <img src="/assets/img/FooterImages/success.svg" alt="success" />
            </FormIcon>
            <FormStatusText>Error sending message, please try again</FormStatusText>
          </div>
        </FormSent>
      )}
    </FormComponent>
  );
};

export default Form;
